import React, { FC, Fragment, useState } from 'react';
import { Order } from 'models/order';
import { NetworkStatus, useQuery } from '@apollo/client';
import Loader from 'components/shared/loader/loader.component';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { DeliveryScheduleNoDeadlineOrdersTemplate } from 'apollo/templates/delivery-schedule/query.graphql';
import NoDeadlineOrdersList from 'components/delivery-schedule/no-deadline-orders-list.component';
import SendToClinicDialog from 'components/orders/order-details/send-to-clinic-dialog.component';
import ChangeInternalDeliveryDateDialog from 'components/orders/order-details/change-internal-delivery-date-dialog.component';
import ChangeExternalDeliveryDateDialog from 'components/orders/order-details/change-external-delivery-date-dialog.component';
import { OrderSortColumn, SortDirection } from 'components/delivery-schedule/use-order-sort.hook';

type DeliveryScheduleNoDeadlineOrdersProps = {
    onBackArrow: () => void;
    isExternal: boolean;
};
const DeliveryScheduleNoDeadlineOrders: FC<DeliveryScheduleNoDeadlineOrdersProps> = ({
    onBackArrow,
    isExternal,
}: DeliveryScheduleNoDeadlineOrdersProps) => {
    const [orders, setOrders] = useState<Order[]>([]);
    const [lastOffset, setLastOffset] = useState(0);
    const [sortColumn, setSortColumn] = useState<OrderSortColumn>('deliveryDate');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
    const [openSentByLabDialog, setOpenSentByLabDialog] = useState(false);
    const [changeInternalDeliveryDateDialogOpen, setChangeInternalDeliveryDateDialogOpen] = useState(false);
    const [changeExternalDeliveryDateDialogOpen, setChangeExternalDeliveryDateDialogOpen] = useState(false);
    const [order, setOrder] = useState<Order>({} as Order);

    const { loading, error, fetchMore, networkStatus } = useQuery(DeliveryScheduleNoDeadlineOrdersTemplate, {
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
        variables: { isExternal: isExternal, limit: 20, offset: 0, sortBy: sortColumn, sortDirection: sortDirection },
        onCompleted: ({ deliveryScheduleNoDeadlineOrders }) => {
            setOrders(deliveryScheduleNoDeadlineOrders);
            setLastOffset(deliveryScheduleNoDeadlineOrders.length);
        },
    });

    const handleFetchMore = () => {
        if (fetchMore) {
            fetchMore({
                variables: {
                    offset: lastOffset,
                },
            });
        }
    };

    const handleSort = (column: OrderSortColumn) => {
        const newDirection = column === sortColumn && sortDirection === 'asc' ? 'desc' : 'asc';
        setOrders([]);
        setLastOffset(0);
        setSortColumn(column);
        setSortDirection(newDirection);
    };

    const handleSentByClinicDialog = (order: Order) => {
        setOrder(order);
        setOpenSentByLabDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenSentByLabDialog(false);
        setChangeInternalDeliveryDateDialogOpen(false);
        setChangeExternalDeliveryDateDialogOpen(false);
        setOrder({} as Order);
    };

    const handleInternalDeliveryDate = (order: Order) => {
        setOrder(order);
        setChangeInternalDeliveryDateDialogOpen(true);
    };

    const handleExternalDeliveryDate = (order: Order) => {
        setOrder(order);
        setChangeExternalDeliveryDateDialogOpen(true);
    };

    const handleUpdateList = (order: Order) => {
        const ordersArr = JSON.parse(JSON.stringify(orders));
        const index = ordersArr.findIndex((ord: Order) => ord._id === order._id);
        if (index !== -1) {
            ordersArr.splice(index, 1);
            setOrders(ordersArr);
        }
    };

    return (
        <Fragment>
            {error ? <AlertSnackbar type="error" message={error?.message} /> : undefined}
            {loading && networkStatus !== NetworkStatus.fetchMore ? (
                <Loader />
            ) : (
                <NoDeadlineOrdersList
                    onSentByClinicDialog={handleSentByClinicDialog}
                    onChangeInternalDeliveryDate={handleInternalDeliveryDate}
                    onChangeExternalDeliveryDate={handleExternalDeliveryDate}
                    onBackArrow={onBackArrow}
                    orders={orders}
                    onFetchMore={handleFetchMore}
                    isExternal={isExternal}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                />
            )}

            {openSentByLabDialog ? (
                <SendToClinicDialog
                    isUpdateCache={true}
                    onUpdate={handleUpdateList}
                    open={openSentByLabDialog}
                    onClose={handleCloseDialog}
                    orderId={order._id}
                />
            ) : undefined}

            <ChangeInternalDeliveryDateDialog
                isUpdateCache={true}
                onUpdate={handleUpdateList}
                open={changeInternalDeliveryDateDialogOpen}
                onClose={handleCloseDialog}
                order={order}
            />
            {order.deliveryDetails ? (
                <ChangeExternalDeliveryDateDialog
                    isUpdateCache={true}
                    onUpdate={handleUpdateList}
                    open={changeExternalDeliveryDateDialogOpen}
                    onClose={handleCloseDialog}
                    order={order}
                />
            ) : undefined}
        </Fragment>
    );
};

export default DeliveryScheduleNoDeadlineOrders;
